import React, { useCallback, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { toast } from 'react-toastify';
import monitoringService from '../../services/monitoringService';
import projectService from '../../services/projectService';
import { formatCategory } from '../inspections/categoryOptions';
import MonitoringNav from './MonitoringNav';
import './Monitoring.css';

const MonitoringTrends = () => {
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [summary, setSummary] = useState({ series: [], byCategory: [], byParameter: [], totalEntries: 0 });
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (projectFilter !== 'all') params.projectId = projectFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const data = await monitoringService.getEntriesSummary(params);
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch monitoring trends:', error);
      toast.error(error.response?.data?.error || 'Failed to load monitoring trends');
    } finally {
      setLoading(false);
    }
  }, [projectFilter, categoryFilter]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const trendOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['Within range', 'Out of range', 'Not applicable'], bottom: 0 },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '8%', containLabel: true },
    xAxis: { type: 'category', data: summary.series.map((s) => s.date) },
    yAxis: { type: 'value' },
    series: [
      {
        name: 'Within range',
        type: 'bar',
        stack: 'total',
        data: summary.series.map((s) => s.withinRange),
        itemStyle: { color: '#16a34a' },
      },
      {
        name: 'Out of range',
        type: 'bar',
        stack: 'total',
        data: summary.series.map((s) => s.outOfRange),
        itemStyle: { color: '#dc2626' },
      },
      {
        name: 'Not applicable',
        type: 'bar',
        stack: 'total',
        data: summary.series.map((s) => s.notApplicable),
        itemStyle: { color: '#9ca3af' },
      },
    ],
  };

  const categoryOption = {
    tooltip: { trigger: 'item' },
    series: [
      {
        type: 'pie',
        radius: ['45%', '75%'],
        data: summary.byCategory.map((c) => ({
          name: formatCategory(c.category),
          value: c.withinRange + c.outOfRange + c.notApplicable,
        })),
        label: { formatter: '{b}: {c}' },
      },
    ],
  };

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <h1><i className="fas fa-shield-alt"></i> Monitoring</h1>
          <p className="header-subtitle">Compliance trends across all monitored areas</p>
        </div>
      </div>

      <MonitoringNav />

      <div className="monitoring-controls">
        <select className="filter-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
          <option value="all">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All categories</option>
          {summary.byCategory.map((c) => (
            <option key={c.category} value={c.category}>{formatCategory(c.category)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading trends...</h3>
        </div>
      ) : summary.totalEntries === 0 ? (
        <div className="empty-state">
          <i className="fas fa-chart-line"></i>
          <h3>Not enough data yet</h3>
          <p>Trends will appear once daily log entries have been submitted.</p>
        </div>
      ) : (
        <>
          <div className="trend-grid">
            <div className="trend-card">
              <h3>Compliance Trend</h3>
              <ReactECharts option={trendOption} style={{ height: '320px' }} />
            </div>
            <div className="trend-card">
              <h3>Entries by Category</h3>
              <ReactECharts option={categoryOption} style={{ height: '320px' }} />
            </div>
          </div>

          <div className="trend-card">
            <h3>By Parameter</h3>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Category</th>
                  <th>Average</th>
                  <th>Within range</th>
                  <th>Out of range</th>
                  <th>N/A</th>
                </tr>
              </thead>
              <tbody>
                {summary.byParameter.map((p) => (
                  <tr key={p.parameterId}>
                    <td>{p.name}{p.unit ? ` (${p.unit})` : ''}</td>
                    <td><span className="category-badge">{formatCategory(p.category)}</span></td>
                    <td>{p.average !== null ? p.average : '—'}</td>
                    <td>{p.withinRange}</td>
                    <td>{p.outOfRange}</td>
                    <td>{p.notApplicable}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default MonitoringTrends;
